import { 
  ComposedChart,
  Bar, 
  Line,
  XAxis, 
  YAxis, 
  CartesianGrid, 
  Tooltip, 
  ResponsiveContainer,
  Legend
} from "recharts";
import { Gauge } from "lucide-react";
import { cn } from "@/lib/utils";
import type { LeverageDailyPoint } from "@/lib/hyperliquid";

interface LeverageChartProps {
  data?: LeverageDailyPoint[];
  loading?: boolean;
  className?: string;
}

export const LeverageChart = ({ 
  data = [], 
  loading = false,
  className 
}: LeverageChartProps) => {
  if (loading) {
    return (
      <div className={cn("stat-card h-[320px]", className)}>
        <div className="flex items-center justify-between mb-6">
          <div className="h-5 w-32 bg-muted/50 rounded animate-pulse" />
          <div className="h-4 w-20 bg-muted/50 rounded animate-pulse" />
        </div>
        <div className="h-56 w-full bg-muted/30 rounded animate-pulse" />
      </div>
    );
  }

  const chartData = data.map(d => ({
    date: new Date(d.day).toLocaleDateString('en-US', { month: 'short', day: 'numeric' }),
    avgLeverage: d.avgLeverage,
    maxLeverage: d.maxLeverage,
  }));

  // Compute summary stats
  const avgLeverageOverall = chartData.length > 0 
    ? chartData.reduce((sum, d) => sum + d.avgLeverage, 0) / chartData.length 
    : 0;
  const maxLeverageOverall = chartData.length > 0
    ? Math.max(...chartData.map(d => d.maxLeverage))
    : 0;

  const CustomTooltip = ({ active, payload, label }: any) => {
    if (active && payload && payload.length) {
      return (
        <div className="bg-popover/95 backdrop-blur-sm border border-border/50 rounded-lg p-3 shadow-xl">
          <p className="text-xs text-muted-foreground mb-1.5">{label}</p>
          <div className="space-y-1">
            <div className="flex items-center gap-2">
              <div className="w-2 h-2 rounded-full bg-primary" />
              <span className="text-sm font-mono">
                Avg: <span className="font-bold">{payload[0]?.value?.toFixed(1)}x</span>
              </span>
            </div>
            <div className="flex items-center gap-2">
              <div className="w-2 h-2 rounded-full bg-amber-500" />
              <span className="text-sm font-mono">
                Max: <span className="font-bold">{payload[1]?.value?.toFixed(1)}x</span>
              </span>
            </div>
          </div>
        </div>
      );
    }
    return null;
  };

  if (chartData.length === 0) {
    return (
      <div className={cn("stat-card h-[320px]", className)}>
        <div className="flex items-center justify-between mb-6">
          <div>
            <h3 className="text-base font-semibold mb-1">Daily Leverage</h3>
            <span className="text-xs text-muted-foreground">Position sizing relative to account</span>
          </div>
        </div>
        <div className="h-56 flex items-center justify-center text-muted-foreground text-sm">
          No leverage data available
        </div>
      </div>
    );
  }

  return (
    <div className={cn("stat-card h-[320px] group", className)}>
      <div className="relative z-10">
        <div className="flex items-center justify-between mb-4">
          <div>
            <div className="flex items-center gap-2 mb-1">
              <h3 className="text-base font-semibold">Daily Leverage</h3>
              <div className="flex items-center gap-1 text-xs font-medium px-2 py-0.5 rounded-full bg-primary/10 text-primary">
                <Gauge className="w-3 h-3" />
                {chartData.length} days
              </div>
            </div>
            <span className="text-xs text-muted-foreground">Position sizing relative to account</span>
          </div>
          <div className="text-right">
            <div className="text-lg font-bold font-mono">
              {avgLeverageOverall.toFixed(1)}x
            </div>
            <div className="text-xs text-muted-foreground">Avg / {maxLeverageOverall.toFixed(1)}x Max</div>
          </div>
        </div>
        <div className="h-56">
          <ResponsiveContainer width="100%" height="100%">
            <ComposedChart data={chartData}>
              <defs>
                <linearGradient id="leverageBarGradient" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor="hsl(var(--primary))" stopOpacity={0.6} />
                  <stop offset="100%" stopColor="hsl(var(--primary))" stopOpacity={0.2} />
                </linearGradient>
              </defs>
              <CartesianGrid 
                strokeDasharray="3 3" 
                stroke="hsl(var(--border))" 
                strokeOpacity={0.3}
                vertical={false}
              />
              <XAxis 
                dataKey="date" 
                axisLine={false}
                tickLine={false}
                tick={{ fill: "hsl(var(--muted-foreground))", fontSize: 10 }}
                dy={10}
                interval="preserveStartEnd"
              />
              <YAxis 
                axisLine={false}
                tickLine={false}
                tick={{ fill: "hsl(var(--muted-foreground))", fontSize: 10 }}
                tickFormatter={(value) => `${value}x`}
                dx={-5}
                domain={[0, 'auto']}
              />
              <Tooltip content={<CustomTooltip />} cursor={{ fill: "hsl(var(--muted))", opacity: 0.2 }} />
              <Bar 
                dataKey="avgLeverage" 
                fill="url(#leverageBarGradient)"
                radius={[4, 4, 0, 0]}
                name="Avg Leverage"
              />
              <Line
                type="monotone"
                dataKey="maxLeverage"
                stroke="hsl(var(--amber-500, #f59e0b))"
                strokeWidth={2}
                dot={false}
                name="Max Leverage"
              />
            </ComposedChart>
          </ResponsiveContainer>
        </div>
      </div>
    </div>
  );
};
